import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/lib/Button';
import classnames from 'classnames';

import Icon from '../../general/Icon';
import t from '../../../lib/I18n';
import SearchExpander from '../SearchExpander';
import CurrentPositionMarker from './CurrentPositionMarker';
import StationFinderActions from '../../../actions/StationFinderActions';
import { StationFinderSearchInteractions } from '../../../constants/AppConstants';
import { useMap } from '../../openMap/MapContext';
import config from '../../../config';
import { loadScript } from '../../../helpers/ExternalScript';

const SearchOverlay = ({
   isExpanded,
   isSearchingStations,
   isMobile,
   onClear,
   onSelectPosition,
   onSearch,
   error,
   positionMarkerCoords,
   setPositionMarkerCoords,
}) => {
    const [labelState, setLabelState] = useState(null);
    const [isLocating, setIsLocating] = useState(false);
    const [isLeafletGeolocationLoaded, setIsLeafletGeolocationLoaded] = useState(false);
    const leafletGeolocationUrl = config.get('leafletGeolocationUrl');

    useEffect(() => {
        async function getLeafleftMapScript() {
            await loadScript(leafletGeolocationUrl);
            setIsLeafletGeolocationLoaded(true);
        }
        getLeafleftMapScript();
    }, [leafletGeolocationUrl]);

    const { map } = useMap();

    const LeafletgeoSearch = () => {
        // eslint-disable-next-line
        const provider = new GeoSearch.OpenStreetMapProvider({
            params: {
                'accept-language': "en",
                countrycodes: "gb",
                addressdetails: 1,
            }
        });
    
        // eslint-disable-next-line
        const searchControl = new GeoSearch.GeoSearchControl({
            provider,
            showMarker: false,
            searchLabel: t('stationFinder.search.placeholder'),
            resultFormat: ({ result }) => {
                return result.label
            },
        });

        map.addControl(searchControl);

        map.on('geosearch/showlocation', (e) => {
            const { y, x } = e.location;
            setPositionMarkerCoords({ lat: y, lng: x });
            
            const errorBlock = document.querySelector('.stationFinder__search-error');

            if (errorBlock) {
                errorBlock.classList.add("hide");
            }
        });
    
        useEffect(() => {
            return () => map.removeControl(searchControl);
        }, []); 
    
        return null;
    }

    const onSearchClear = () => {
        onClear();
        setLabelState(null);
        setPositionMarkerCoords(null);
        StationFinderActions.clearStations();
        if (error) {
            StationFinderActions.clearError();
        }
    };

    const setCurrentPosition = async (location) => {
        setPositionMarkerCoords(location);
        onSelectPosition(location);
        await onSearch(location, StationFinderSearchInteractions.CURRENT_LOCATION);
        dismissOverlay();
        setIsLocating(false);
    };

    const dismissError = () => {
        StationFinderActions.clearError();
    };

    const dismissOverlay = () => {
        setLabelState(null);
        StationFinderActions.setExpanded(false);
    };

    const errorBlock = document.querySelector('.stationFinder__search-error');
    const errorText = document.querySelector('.stationFinder__search-error .alert-warning');

    if (isExpanded) {
       setTimeout(function () {
            const geosearch = document.querySelector('.geosearch');
            
            if (geosearch) {
                const input = geosearch.querySelector('input');

                input.addEventListener("keydown", () => {
                    errorBlock.classList.remove("hide");

                    setTimeout(function () {
                        const results = document.querySelector('.geosearch .results');

                        if (!errorBlock.classList.contains('hide') && input.value && (results.childNodes.length === 0)) {
                            errorBlock.style.display = 'block';
                            if (errorText) {
                                errorText.innerHTML = `No results for ${input.value}`;
                            }
                        } else {
                            errorBlock.style.display = 'none';
                            if (errorText) {
                                errorText.innerHTML = '';
                            }
                        }
                    }, 1000);
                });
            }
       }, 1000);
    } else {
        if (errorBlock) {
            errorBlock.style.display = 'none';
        }
    }
    
    return (<div
        id="stationFinder-overlay"
        className={ classnames('stationFinder__search-overlay', {
            expanded: isExpanded,
            mobile: isMobile
        }) }
        tabIndex={ -1 }
    >
        { (labelState && !isLocating) && <Button className="stationFinder__search-clear" onClick={ onSearchClear }><Icon icon="clear" /></Button> }
        <div
            className="input-group"
            id="search-overlay"
            role="region"
            aria-label={t('stationFinder.search.placeholder')}
        >
            <SearchExpander
                isExpanded={ isExpanded }
                isSearching={ isSearchingStations }
                onExpand={ () => StationFinderActions.setExpanded(!isExpanded) }
                isDisabled={ !isLeafletGeolocationLoaded }/>
            {map && isExpanded && <LeafletgeoSearch />}
        </div>
        
        <div style={{ display: 'none' }} onClick={ dismissError } className="stationFinder__search-error">
            <div className="icon-margin">
                <Icon icon="error" />
            </div>
            <div className="alert-warning">
                
            </div>
        </div>

        { positionMarkerCoords && (
            <CurrentPositionMarker 
                coords={positionMarkerCoords} 
                onMove={setCurrentPosition} 
            />) 
        }
    </div>);
};

SearchOverlay.propTypes = {
    isExpanded: PropTypes.bool.isRequired,
    isSearchingStations: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    onClear: PropTypes.func.isRequired,
    onSelectPosition: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    error: PropTypes.instanceOf(Error),
    positionMarkerCoords: PropTypes.objectOf({
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
    }),
    setPositionMarkerCoords: PropTypes.func.isRequired,
};

export default React.memo(SearchOverlay);



// WEBPACK FOOTER //
// ./src/components/stationFinder/OpenStreetMaps/SearchOverlay.js